import React, { type CSSProperties, type ReactNode } from "react";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { cx, useKitDisabled, useKitFieldStatus, useKitSize } from "./fieldContext";
import { KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_ENTER } from "./keyboardKeys";
const DECIMAL_POINT = ".";
const MINUS_SIGN = "-";
const DECIMAL_COMMA_PARTS = 2;
const MAX_TO_FIXED_DIGITS = 20;

/*
 * Zahlenfeld des Kits (Plan 2026-10-13-antd-ausbau, Stufe 6; Ersatz für Antd `InputNumber`). Die Eingabe bleibt
 * während des Tippens Text, damit Komma-Eingaben und Zwischenstände („1,“, „-“) nicht springen; `onChange`
 * bekommt nur gültige Zahlen im Bereich (wie Antd). Beim Verlassen wird auf `min`/`max` begrenzt und auf
 * `precision` gerundet. `formatter`/`parser` und `decimalSeparator` wie Antd.
 */

type NumberValue = number | null;

export interface InputNumberProps<T extends number | string = number> {
  value?: T | null;
  defaultValue?: T | null;
  onChange?: (value: T | null) => void;
  min?: number;
  max?: number;
  step?: number | string;
  precision?: number;
  decimalSeparator?: string;
  formatter?: (value: T | undefined, info: { userTyping: boolean; input: string }) => string;
  parser?: (displayValue: string | undefined) => T;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  controls?: boolean | { upIcon?: ReactNode; downIcon?: ReactNode };
  keyboard?: boolean;
  addonBefore?: ReactNode;
  addonAfter?: ReactNode;
  prefix?: ReactNode;
  suffix?: ReactNode;
  size?: "small" | "middle" | "large";
  status?: "error" | "warning" | "";
  variant?: "outlined" | "filled" | "borderless" | "underlined";
  className?: string;
  rootClassName?: string;
  style?: CSSProperties;
  id?: string;
  name?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  tabIndex?: number;
  onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onStep?: (value: T, info: { offset: number; type: "up" | "down" }) => void;
  /** Antd-Option ohne Wirkung im Kit (Zahlen bleiben `number`). */
  stringMode?: boolean;
  [dataOrAria: `data-${string}` | `aria-${string}`]: unknown;
}

const decimals = (value: number | string | undefined) => {
  const text = String(value ?? "");
  const dot = text.indexOf(".");
  return dot < 0 ? 0 : text.length - dot - 1;
};

function toNumber(value: number | string | null | undefined): NumberValue {
  if (value === null || value === undefined || value === "") return null;
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

/** Zahlenfeld mit Komma-Eingabe, Pfeiltasten und optionalen Schrittknöpfen. */
const InputNumberImpl = React.forwardRef<HTMLInputElement, InputNumberProps<number>>(function InputNumber(props, ref) {
  const {
    value, defaultValue, onChange, min = Number.MIN_SAFE_INTEGER, max = Number.MAX_SAFE_INTEGER, step = 1, precision, decimalSeparator,
    formatter, parser, placeholder, disabled, readOnly, controls = true, keyboard = true, addonBefore, addonAfter, prefix, suffix,
    size, status, variant, className, rootClassName, style, onPressEnter, onBlur, onFocus, onKeyDown, onStep, stringMode: _stringMode,
    ...rest
  } = props;
  const mergedDisabled = useKitDisabled(disabled);
  const mergedSize = useKitSize(size);
  const mergedStatus = useKitFieldStatus(status);
  const [innerValue, setInnerValue] = React.useState<NumberValue>(() => toNumber(defaultValue));
  const current = value !== undefined ? toNumber(value) : innerValue;
  const [focused, setFocused] = React.useState(false);
  const [draft, setDraft] = React.useState<string | null>(null);

  const format = React.useCallback((number: NumberValue, userTyping = false): string => {
    if (number === null) return "";
    // display-format: Eingabetext des Felds; das Trennzeichen setzt decimalSeparator.
    let text = precision !== undefined ? number.toFixed(precision) : String(number);
    if (decimalSeparator) text = text.replace(".", decimalSeparator);
    // Antd reicht dem Formatierer die Zahl als Text (typisiert als `T`).
    return formatter ? formatter(text as unknown as number, { userTyping, input: text }) : text;
  }, [decimalSeparator, formatter, precision]);

  const parse = React.useCallback((text: string): { empty: boolean; number: NumberValue } => {
    const raw = parser ? parser(text) : text;
    if (typeof raw === "number") return { empty: false, number: Number.isFinite(raw) ? raw : null };
    let normalized = String(raw ?? "").replace(/\s/g, "");
    if (decimalSeparator && decimalSeparator !== DECIMAL_POINT) normalized = normalized.split(decimalSeparator).join(DECIMAL_POINT);
    else if (!normalized.includes(DECIMAL_POINT) && normalized.split(",").length === DECIMAL_COMMA_PARTS) normalized = normalized.replace(",", DECIMAL_POINT);
    if (normalized === "" || normalized === MINUS_SIGN) return { empty: true, number: null };
    const number = Number(normalized);
    return { empty: false, number: Number.isFinite(number) ? number : null };
  }, [decimalSeparator, parser]);

  const round = (number: number) => {
    const digits = precision ?? Math.max(decimals(step), decimals(number));
    return Number(number.toFixed(Math.min(digits, MAX_TO_FIXED_DIGITS)));
  };
  const clamp = (number: number) => Math.min(max, Math.max(min, number));

  const emit = (next: NumberValue) => {
    if (next === current) return;
    if (value === undefined) setInnerValue(next);
    onChange?.(next);
  };

  const text = focused && draft !== null ? draft : format(current);

  const stepBy = (direction: 1 | -1) => {
    if (mergedDisabled || readOnly) return;
    const base = current ?? 0;
    const offset = Number(step) * direction;
    const next = round(clamp(base + offset));
    setDraft(null);
    emit(next);
    onStep?.(next, { offset: Math.abs(offset), type: direction > 0 ? "up" : "down" });
  };

  const fieldClass = cx(
    "ck-field",
    "ck-field--affix",
    "ck-input-number",
    mergedSize === "small" && "ck-field--sm",
    mergedSize === "large" && "ck-field--lg",
    variant && `ck-field--${variant}`,
    mergedStatus && `is-${mergedStatus}`,
    mergedDisabled && "is-disabled",
    focused && "is-focused",
  );
  const showControls = controls !== false && !readOnly;
  const hasAddon = addonBefore != null || addonAfter != null;
  const hasWidth = style?.width !== undefined;

  const field = (
    <span className={cx(fieldClass, !hasAddon && className, !hasAddon && rootClassName)} style={hasAddon ? (hasWidth ? { width: "100%" } : undefined) : style}>
      {prefix != null ? <span className="ck-field-prefix">{prefix}</span> : null}
      <input
        ref={ref}
        className="ck-field-input ck-input-number-input"
        inputMode="decimal"
        autoComplete="off"
        role="spinbutton"
        aria-valuemin={min === Number.MIN_SAFE_INTEGER ? undefined : min}
        aria-valuemax={max === Number.MAX_SAFE_INTEGER ? undefined : max}
        aria-valuenow={current ?? undefined}
        aria-invalid={mergedStatus === "error" || undefined}
        placeholder={placeholder}
        disabled={mergedDisabled}
        readOnly={readOnly}
        value={text}
        {...rest}
        onChange={(event) => {
          const input = event.target.value;
          setDraft(input);
          const parsed = parse(input);
          if (parsed.empty) { emit(null); return; }
          if (parsed.number !== null && parsed.number >= min && parsed.number <= max) emit(parsed.number);
        }}
        onFocus={(event) => { setFocused(true); setDraft(format(current, true)); onFocus?.(event); }}
        onBlur={(event) => {
          const parsed = parse(event.target.value);
          if (parsed.empty) emit(null);
          else if (parsed.number !== null) emit(round(clamp(parsed.number)));
          setFocused(false);
          setDraft(null);
          onBlur?.(event);
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (event.defaultPrevented) return;
          if (event.key === KEY_ENTER) {
            const parsed = parse(event.currentTarget.value);
            if (parsed.number !== null) { const next = round(clamp(parsed.number)); emit(next); setDraft(format(next, true)); }
            onPressEnter?.(event);
          } else if (keyboard && (event.key === KEY_ARROW_UP || event.key === KEY_ARROW_DOWN)) {
            event.preventDefault();
            stepBy(event.key === "ArrowUp" ? 1 : -1);
          }
        }}
      />
      {suffix != null ? <span className="ck-field-suffix">{suffix}</span> : null}
      {showControls ? (
        <span className="ck-input-number-handler-wrap" aria-hidden="true">
          <span className={cx("ck-input-number-handler", "ck-input-number-handler-up", current !== null && current >= max && "is-disabled")} onMouseDown={(event) => { event.preventDefault(); stepBy(1); }}>
            {typeof controls === "object" && controls.upIcon ? controls.upIcon : <UpOutlined />}
          </span>
          <span className={cx("ck-input-number-handler", "ck-input-number-handler-down", current !== null && current <= min && "is-disabled")} onMouseDown={(event) => { event.preventDefault(); stepBy(-1); }}>
            {typeof controls === "object" && controls.downIcon ? controls.downIcon : <DownOutlined />}
          </span>
        </span>
      ) : null}
    </span>
  );

  if (!hasAddon) return field;
  return (
    <span className={cx("ck-field-group", "ck-input-number-group", rootClassName, className)} style={style}>
      {addonBefore != null ? <span className="ck-field-addon">{addonBefore}</span> : null}
      {field}
      {addonAfter != null ? <span className="ck-field-addon">{addonAfter}</span> : null}
    </span>
  );
});

/** Generisch wie Antd (`InputNumber<number>`); das Kit liefert Zahlen. */
export const InputNumber = InputNumberImpl as unknown as <T extends number | string = number>(
  props: InputNumberProps<T> & React.RefAttributes<HTMLInputElement>,
) => React.ReactElement;
